/**
 * `purchaseOrders.*` wire contract (Phase G1).
 *
 * Mirrors `bills.ts`: paise cross the wire as stringified bigint (`moneyPaise`),
 * line inputs are operator strings the domain re-computes. A PO is numbered at
 * `send` (gap-allowed, system-decisions §11) — drafts carry a null `poNumber`.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

export const poStatus = z.enum([
  "draft",
  "sent",
  "confirmed",
  "partially_received",
  "received",
  "closed",
  "cancelled",
]);

export const poSendMode = z.enum(["mark-sent", "email", "whatsapp"]);

export const poLineInput = z.object({
  itemId: uuid.nullable().optional(),
  description: z.string().trim().min(1).max(500),
  hsn: z.string().trim().max(8).default(""),
  qty: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,3})?$/),
  unit: z.string().trim().min(1).max(16),
  rate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/),
  discountRate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/)
    .default("0"),
  taxRate: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{0,2})?$/)
    .default("18"),
});

export const poLineDetail = z.object({
  id: uuid,
  position: z.number().int().positive(),
  itemId: uuid.nullable(),
  description: z.string(),
  hsn: z.string(),
  qtyMilli: z.number().int().nonnegative(),
  unit: z.string(),
  ratePaise: moneyPaise,
  discountBps: z.number().int().nonnegative(),
  taxRateBps: z.number().int().nonnegative(),
  grossPaise: moneyPaise,
  discountPaise: moneyPaise,
  taxablePaise: moneyPaise,
  taxPaise: moneyPaise,
  lineTotalPaise: moneyPaise,
});

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: poStatus.optional(),
  vendorId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().trim().min(1).max(80).optional(),
});

export const createInput = z.object({
  vendorId: uuid,
  orderDate: isoDate,
  expectedDate: isoDate.nullable().optional(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  vendorNote: z.string().trim().max(2000).nullable().optional(),
  internalNote: z.string().trim().max(2000).nullable().optional(),
  lineItems: z.array(poLineInput).min(1).max(200).optional(),
});

export const createOutput = z.object({ id: uuid });

export const getInput = z.object({ id: uuid });

export const poDetail = z.object({
  id: uuid,
  vendorId: uuid,
  vendorName: z.string(),
  vendorGstin: z.string().nullable(),
  vendorCity: z.string().nullable(),
  vendorState: z.string().nullable(),
  vendorPhone: z.string().nullable(),
  vendorEmail: z.string().nullable(),
  vendorPhoneWhatsapp: z.boolean(),
  whatsappBusinessEnabled: z.boolean(),
  poNumber: z.string().nullable(),
  orderDate: isoDate,
  expectedDate: isoDate.nullable(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  status: poStatus,
  vendorNote: z.string().nullable(),
  internalNote: z.string().nullable(),
  createdAt: z.string().datetime(),
  createdByActorId: z.string(),
  createdByActorType: z.string(),
  lineItems: z.array(poLineDetail),
});

export const getOutput = poDetail;

export const updateInput = z.object({
  id: uuid,
  orderDate: isoDate,
  expectedDate: isoDate.nullable().optional(),
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  vendorNote: z.string().trim().max(2000).nullable().optional(),
  internalNote: z.string().trim().max(2000).nullable().optional(),
  lineItems: z.array(poLineInput).min(1).max(200).optional(),
});

export const updateOutput = z.object({ id: uuid });

export const sendInput = z.object({
  id: uuid,
  sendMode: poSendMode.default("mark-sent"),
  /**
   * Resubmit token (ADR-0031). When a configured `purchase_order` approval
   * chain gated the first send, the operator approves in the inbox and resends
   * with the `pending_approvals.id` returned as `approvalId` — the domain
   * verifies it and bypasses the gate.
   */
  approvalId: uuid.optional(),
});

export const sendOutput = z.object({
  id: uuid,
  /** `sent` = numbered + issued; `awaiting_approval` = a chain is pending. */
  outcome: z.enum(["sent", "awaiting_approval"]),
  /** `sent` once issued; stays `draft` while awaiting approval. */
  status: poStatus,
  /** The allocated PO number — null while awaiting approval (no number burned). */
  poNumber: z.string().nullable(),
  /** Filed `pending_approvals.id` when `outcome='awaiting_approval'`, else null. */
  approvalId: uuid.nullable(),
  /** Populated only when the operator picked email or WhatsApp. */
  send: z
    .object({
      channel: z.enum(["email", "whatsapp"]),
      notificationId: uuid,
      deduplicated: z.boolean(),
    })
    .nullable(),
});

export const transitionStatusInput = z.object({
  id: uuid,
  status: poStatus,
});

export const transitionStatusOutput = z.object({
  id: uuid,
  status: poStatus,
});

export const deleteDraftInput = z.object({ id: uuid });

export const deleteDraftOutput = z.object({ id: uuid });

export const voidInput = z.object({
  id: uuid,
  reason: z.string().trim().max(500).nullable().optional(),
});

export const voidOutput = z.object({
  id: uuid,
  status: poStatus,
});

export const listOutput = paged(
  z.object({
    id: uuid,
    vendorId: uuid,
    vendorName: z.string(),
    vendorGstin: z.string().nullable(),
    vendorCity: z.string().nullable(),
    vendorState: z.string().nullable(),
    poNumber: z.string().nullable(),
    orderDate: isoDate,
    expectedDate: isoDate.nullable(),
    totalPaise: moneyPaise,
    status: poStatus,
    createdAt: z.string().datetime(),
  }),
).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    confirmed: z.number().int().nonnegative(),
    partially_received: z.number().int().nonnegative(),
    received: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
  }),
  kpis: z.object({
    openCount: z.number().int().nonnegative(),
    openValuePaise: moneyPaise,
    draftCount: z.number().int().nonnegative(),
    awaitingConfirmationCount: z.number().int().nonnegative(),
    expectedThisWeekCount: z.number().int().nonnegative(),
    topVendorName: z.string().nullable(),
    topVendorPoCount: z.number().int().nonnegative(),
  }),
});
